import { Auditor, EventAuditor, OrganizationAuditor } from "../../models";
import { deleteAuditor } from "./Auditor";
import { deleteEventAuditor, getEventAuditorsByPhoneNumberSearch } from "../eventAuditor/EventAuditor";
import { deleteOrganizationAuditor, getOrganizationAuditorByPhoneNumber, getOrganizationAuditorsByPhoneNumberSearch } from "../organizationAuditor/OrganizationAuditor";
import { Result } from "../../interfaces/Result";
import formatResult from "../ReturnType/ReturnType";

/**
 * Delete an auditor and all related EventAuditor and OrganizationAuditor records
 * @param auditor - The auditor to delete
 * @returns Result indicating success or failure
 */
export async function deleteAuditorWithRelatedRecords(auditor: Auditor): Promise<Result> {
    if (!auditor?.id) {
        return formatResult(false, "Auditor", null, "Auditor is required");
    }

    try {
        let deletedEventAuditors = 0;
        let deletedOrgAuditors = 0;

        // Step 1: Delete all EventAuditor records connected to this auditor
        if (auditor.phoneNumber) {
            const eventAuditorsResult = await getEventAuditorsByPhoneNumberSearch(auditor.phoneNumber);
            
            if (eventAuditorsResult.isSuccess) {
                const eventAuditors: EventAuditor[] = eventAuditorsResult.result || [];
                
                for (const eventAuditor of eventAuditors) {
                    if (eventAuditor.auditorId === auditor.id) {
                        const deleteResult = await deleteEventAuditor({id: eventAuditor.id});
                        if (deleteResult.isSuccess) {
                            deletedEventAuditors++;
                        } else {
                            console.error(`Failed to delete EventAuditor ${eventAuditor.id}:`, deleteResult.message);
                        }
                    }
                }
            }
        }

        // Step 2: Delete all OrganizationAuditor records connected to this auditor
        if (auditor.phoneNumber) {
            const orgAuditorsResult = await getOrganizationAuditorsByPhoneNumberSearch(auditor.phoneNumber);
            
            if (orgAuditorsResult.isSuccess) {
                const orgAuditors: OrganizationAuditor[] = Array.isArray(orgAuditorsResult.result) 
                    ? orgAuditorsResult.result 
                    : [orgAuditorsResult.result];
                
                for (const orgAuditor of orgAuditors) {
                    if (orgAuditor && orgAuditor.auditorId === auditor.id) {
                        const deleteResult = await deleteOrganizationAuditor({id: orgAuditor.id});
                        if (deleteResult.isSuccess) {
                            deletedOrgAuditors++;
                        } else {
                            console.error(`Failed to delete OrganizationAuditor ${orgAuditor.id}:`, deleteResult.message);
                        }
                    }
                }
            }
        }

        // Step 3: Delete the auditor record itself
        const deleteAuditorResult = await deleteAuditor({id: auditor.id});
        
        if (!deleteAuditorResult.isSuccess) {
            return formatResult(
                false, 
                "Auditor", 
                null, 
                `Failed to delete auditor: ${deleteAuditorResult.message}`
            );
        }

        return formatResult(
            true, 
            "Auditor", 
            { 
                auditor, 
                deletedEventAuditors, 
                deletedOrgAuditors 
            }, 
            `Successfully deleted auditor and ${deletedEventAuditors} EventAuditor record(s) and ${deletedOrgAuditors} OrganizationAuditor record(s)`
        );

    } catch (error: any) {
        console.error("Error deleting auditor with related records:", error);
        return formatResult(false, "Auditor", error, "An error occurred while deleting the auditor");
    }
}
